import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode
} from 'react';
import { toast } from 'react-toastify';
import ApiServices from '../../shared/Services/apiService';
import { Role } from './roleTypes';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

interface RoleContextType {
  role: Role;
  updateRole: (role: Role) => Promise<void>;
  isUpdating: boolean;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const RoleProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  // Get the itemType from the Redux auth state
  const authItemType = useSelector((state: RootState) => state.auth.itemType);
  const authPk = useSelector((state: RootState) => state.auth.pk);

  // Set initial role based on authItemType (if available), otherwise default to Role.PATIENT
  const [role, setRole] = useState<Role>(
    authItemType ? (authItemType as Role) : Role.PATIENT
  );
  const [isUpdating, setIsUpdating] = useState(false);

  // Update the role state whenever the authItemType from Redux changes
  useEffect(() => {
    if (authItemType) {
      setRole(authItemType as Role);
    }
  }, [authItemType]);

  const updateRole = async (newRole: Role) => {
    setIsUpdating(true);
    try {
      await ApiServices.updateUserType(authPk, newRole);
      setRole(newRole);
      toast.success(`Role updated to ${newRole}`);
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error('Failed to update role. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <RoleContext.Provider value={{ role, updateRole, isUpdating }}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};
